/**
 * app-reducer.js
 * 
 * Extracted from app-context.js so the reducer and initial state
 * can be tested independently via Jest.
 * 
 * The reducer is created via createReducer() which accepts runtime
 * dependencies (currentUser, Firebase references) as a closure,
 * keeping the same semantics as the original inline definition.
 */

import { nanoid } from "nanoid";
import assets from "./assets.js";

const userAgent = typeof window !== "undefined" && typeof window.navigator !== "undefined" ? navigator.userAgent : "";
const isMobile = Boolean(userAgent.match(/Android|BlackBerry|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i));
const isMobileSize = () => {
  return Boolean(
    userAgent.match(/Android|BlackBerry|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i) ||
    (typeof window !== "undefined" && window.innerWidth <= 820)
  );
};

const initialStory = {
  parts: [{
    title: "Sample", order: ["id-4NNPGRgIT6CbzCXLNHerL",
      "id-4NNPGRgIT6CbzCXLNHerM", "id-4NNPGRgIT6CbzCXLNHerN"]
  }],
  title: "Sample Storyboard",
  target_duration: 15,
  pace: 1.5,
  portal_id: nanoid(12),
  num_shots: null,
  created: null,
  deleted_at: null,
  timerStart: null,
  isCompetition: false,
  last_audio: null,
  audio: null,
  last_cut: null,
  is_vertical: false,
  storyStyle: null,
  shots: [{
    header: "Goal",
    description: 'HERO wants X',
    meta: 'Establish character & context',
    image: "https://storage.googleapis.com/v333m-clip/tgt/tour10/th/thmb009a.jpg",
    id: "id-4NNPGRgIT6CbzCXLNHerL",
    deleted_at: null
  },
  {
    header: 'Challenge',
    description: 'Faces OBSTACLE',
    meta: 'Emotional arc before & after obstacle',
    image: "https://storage.googleapis.com/v333m-clip/tgt/tour10/th/thmb004a.jpg",
    id: "id-4NNPGRgIT6CbzCXLNHerM",
    deleted_at: null
  },
  {
    header: "Outcome",
    description: 'This is what happened',
    meta: 'Reveal uncertainty',
    image: "https://storage.googleapis.com/v333m-clip/tgt/tour10/th/thmb001a.jpg",
    id: "id-4NNPGRgIT6CbzCXLNHerN",
    deleted_at: null
  }]
}

export const initialState = {
  story: initialStory,
  app: {
    isLoading: true,
    isMobile,
    isMobileSize: isMobileSize(),
    alerts: {},
    modal: {},
    upload: null,
    importing: null,
    storyImport: null,
    userIsStoryOwner: false,
    userProjectRole: null,  // "owner" | "member" | "community" | null (logged out)
    prioritizeLivePreview: true,
    previewVolume: 1,
    musicVolume: 1,
    forkingStory: null,
    initAndFork: null,
    tmpRef: null,
    updateUserState: null,
    updateMembers: null,
    savingCut: null,
    migratingAudio: null,
    updateCredits: null,
    toggleStoryStar: null,
    createVeneer: null,
    projectCreditTransfer: null,
    movingTake: false,
    portal: {
      id: null,
      requested_id: null,
      requirePass: null,
    },
    loadingPortalId: null,
    portalPassVerified: null,   // portal_id last verified via PassDialog; re-triggers active-story listener
    contributedCount: 0,
    controls: {
      backSelectOpen: false,
      addFabOpen: false,
      flip: false,
      drawerIsOpen: false,
      storiesOpen: false,
      sharedStorysOpen: false,
      inspirationOpen: false,
    },
    cardPreviews: {},
    cardPrice: 2,
    settings: {
      promptsVisible: true,                   // impacts app & stored in firebase, can be set on a settings page. 
      savedPreviewHasAudio: true,
      proxyIsBlackAndWhite: false,
      ytImportIsBlackAndWhite: false,
      userCanUploadLargeFiles: true,
      userCanListenToTakes: true,
      videoImportIncludesAudio: false,
      communityCanVeneer: true,               // impacts app & stored in firebase, an be set on a settings page. 
      communityCanUpload: true,
      communityCanCamera: true,
      communityCanToggleLivePreview: false,   // impacts app & stored in firebase, an be set on a settings page. 
      communityCanViewSavedPreview: true,     // This lets community see high res color (and thus could download it)
      communityCanDownloadTakes: true,
      communityCanDeleteTakes: false,         // this does allow community members to remove from firebase for view, but does not delete files in the backend
      communityCanTrim: false,                // allows any community member to trim takes
      communityCanListenToAudio: true,        //        
      communityCanForkWithMedia: true,
      communityCanShare: true,
      communityCanStarred: true,
      communityCanChangeStoryStyle: false,

      // communityCanSelectTakes: true,          // tested and never rolled out to prod: we disabled suppport of state.app.settings.communityCanSelectTakes - its effectively false till we manage the data model and can confirm on dev
      // communityShotDescriptionEdit: true,  // an idea:  not used yet
      // communityCanDownload: false,         // an idea:  not used yet --  pershot, this has deeper firebase permission settings
    },
    newSettings: false,
  },
  userData: {},
  stories: {
    storyList: null,
    sharedStoryList: [],
    activeStoryRef: null,
    nextStoryMetadata: null,
    activeStory: {},
    activeShotList: [],
    activeTakeList: [],
    activeRefList: [],
    activeVeneerLUT: {},
    albumSourceSet: new Set(),
    starredStories: [],
    activeStoryCredits: null,
    generateStory: null,
    lastActiveStory: null,
    setLastActive: null,
  },
  generate: {
    method: "text2im",
    model: null,
    systemModel: null,  // system-driven override (e.g. refs bump capacity); never written to Firebase
    userRefs: [],
    prompt: null,
    fetched: false
  },
  inspiration: {
    allbumlist: [],
    initialAlbumID: 'ebb61632-8c51-47b3-a2e2-8ccf4373bde4',
    activeAlbumRef: null,
    activeAlbum: {},
    activeAlbumCardList: []
  }
};
